import { UserRole } from '@shared/types/user_role';

import type { RouteState } from '@admin/components/types';
import { currentUser as USER } from '@admin/config/current_user';
import { PERMISSIONS } from '@admin/config/permissions';
import { ROLES } from '@admin/config/roles';
import { ADMIN_DISPATCH } from '@admin/libraries/access_control_catalog.generated';

const IS_IRON_MOUNTAIN_USER = USER.external_group === 'iron_mountain';

const IS_RECRUITER_ONLY = USER.roles.length === 1 && ROLES.has(UserRole.Recruiter);

export const allowed = (state: RouteState) => {
  switch (state) {
    case 'inbox':
    case 'viewTicket':
      return PERMISSIONS.has('staff_ticket:read');
    case 'newTicket':
      return PERMISSIONS.has('staff_ticket:create');
    case 'ticketmanagement':
      return PERMISSIONS.has('ticket_management_config:read');
    case 'smsInbox':
    case 'phoneCalls':
    case 'voicemails':
      return PERMISSIONS.has('cx_inbox:read');
    case 'viewAccount':
      return (
        ROLES.has(UserRole.Admin) ||
        ROLES.has(UserRole.L2Agent) ||
        ROLES.has(UserRole.L1Agent) ||
        ROLES.has(UserRole.Manager) ||
        ROLES.has(UserRole.SelfStorageAssociate)
      );
    case 'leads':
      return PERMISSIONS.has('leads:read');
    case 'accounts':
      return PERMISSIONS.has('account_list:read');
    case 'claims':
    case 'claim':
      return PERMISSIONS.has('claims:read');
    case 'collections':
      return PERMISSIONS.has('collections:read');
    case 'orders':
    case 'transfers':
    case 'calendar':
    case 'itemsExplorer':
      return (
        ROLES.has(UserRole.Admin) ||
        ROLES.has(UserRole.Dispatcher) ||
        ROLES.has(UserRole.L2Agent) ||
        ROLES.has(UserRole.Manager)
      );
    case 'timeClock':
      return PERMISSIONS.has('time_clock:read');
    case 'dispatches':
    case 'dispatcher':
    case 'map':
    case 'viewDispatch':
      return PERMISSIONS.has(ADMIN_DISPATCH.keys.read);
    case 'editDispatch':
      return PERMISSIONS.has(ADMIN_DISPATCH.keys.update);
    case 'users':
    case 'viewUser':
      return PERMISSIONS.has('user:read');
    case 'newUser':
    case 'editUser':
      return PERMISSIONS.has('user:update');
    case 'customerTickets':
    case 'viewCustomerTicket':
      return PERMISSIONS.has('customer_ticket:read');
    case 'gratuities':
      return PERMISSIONS.has('billing:read');
    case 'auctions':
      return PERMISSIONS.has('auction:read');
    case 'auctionUploadNew':
      return PERMISSIONS.has('auction:update');
    case 'editLead':
      return PERMISSIONS.has('leads:update');
    case 'shifts':
      return PERMISSIONS.has('shift:read');
    case 'ptoRequests':
      return PERMISSIONS.has('pto_admin:read');
    case 'selfStorageRateAdjustmentUploader':
    case 'doorToDoorRateAdjustments':
    case 'yieldManagement':
    case 'pricingUploads':
    case 'newPricingUpload':
      return PERMISSIONS.has('pricing_config:read');
    case 'availability':
    case 'palletTypes':
    case 'testOrders':
    case 'hrsRoleDefaults':
    case 'hrsProfileDefaults':
    case 'hrsLocations':
      return PERMISSIONS.has('hrs_config:read');
    case 'facilities':
    case 'viewFacility':
    case 'viewFacilityServiceType':
    case 'warehouses':
    case 'viewWarehouse':
    case 'locations':
    case 'palletRequestsList':
    case 'regions':
    case 'region':
    case 'editRegion':
    case 'depotsList':
    case 'viewDepot':
      return PERMISSIONS.has('facility:read');
    case 'editFacilityServiceType':
    case 'createWarehouse':
    case 'createDepot':
      return PERMISSIONS.has('facility:update');
    case 'featureFlags':
      return PERMISSIONS.has('feature_flag:read');
    case 'iterableExports':
      return PERMISSIONS.has('comms_catalog_export:read');
    case 'teams':
      return PERMISSIONS.has('team:update');
    case 'accessControlCatalog':
      return PERMISSIONS.has('access_control:endpoint_catalog:read');
    case 'accessControlPermissions':
      return PERMISSIONS.has('access_control:permissions_admin:read');
    case 'userSkills':
      return PERMISSIONS.has('user_skill:read');
    case 'trainingUploads':
    case 'trainingUploadAttestations':
      return PERMISSIONS.has('training_upload:read');
    case 'trainingUploadNew':
      return PERMISSIONS.has('training_upload:create');
    case 'trainingUploadEdit':
      return PERMISSIONS.has('training_upload:update');
    case 'sopDocuments':
      return PERMISSIONS.has('sop_document:read');
    case 'partnershipsPartners':
      return PERMISSIONS.has('partnerships:partner:read');
    case 'opsAuditConfigurations':
    case 'opsAuditEditConfiguration':
    case 'notesUploader':
      return ROLES.has(UserRole.Admin) || ROLES.has(UserRole.Auditor);
    case 'redirects':
    case 'coupons':
    case 'referrals':
    case 'qrCodeGenerator':
    case 'affiliates':
    case 'showAffiliate':
      return PERMISSIONS.has('marketing:read');
    case 'newRedirect':
    case 'editRedirect':
    case 'newCoupon':
    case 'showCoupon':
    case 'editCoupon':
    case 'newReferral':
    case 'utmPhones':
    case 'newAffiliate':
    case 'editAffiliate':
      return PERMISSIONS.has('marketing:update');
    case 'warehouseTasks':
    case 'warehouseTaskShow':
      return PERMISSIONS.has('warehouse_ops:read');
    case 'warehouseTaskNew':
      return PERMISSIONS.has('warehouse_ops:create');
    case 'vehicles':
    case 'showVehicle':
      return PERMISSIONS.has('vehicle:read');
    case 'newVehicle':
      return PERMISSIONS.has('vehicle:create');
    case 'editVehicle':
      return PERMISSIONS.has('vehicle:update') || PERMISSIONS.has('vehicle:status:update');
    case 'loads':
    case 'detailedLoad':
    case 'purchaseOrders':
    case 'detailedSku':
      return PERMISSIONS.has('logistics:read');
    case 'createLoad':
    case 'editLoad':
    case 'newSku':
    case 'editSku':
    case 'newPurchaseOrder':
    case 'editPurchaseOrder':
      return PERMISSIONS.has('logistics:update');
    case 'nightTransportPlanner':
    case 'vehicleRouteMatcher':
      return PERMISSIONS.has('dispatching:read');
    case 'landingLocationCityDetails':
    case 'landingLocationCityList':
    case 'landingLocationContent':
    case 'landingLocationNeighborhood':
    case 'landingLocationNeighborhoodList':
    case 'landingLocationPageDetails':
    case 'landingLocationPageList':
    case 'landingLocationRoute':
    case 'landingLocationRouteList':
    case 'landingLocationStateList':
      return PERMISSIONS.has('landing_content:read');
    case 'newLandingLocationCity':
    case 'editLandingLocationCity':
    case 'newLandingLocationContent':
    case 'editLandingLocationContent':
    case 'newLandingLocationNeighborhood':
    case 'editLandingLocationNeighborhood':
    case 'newLandingLocationPage':
    case 'editLandingLocationPage':
    case 'newLandingLocationRoute':
    case 'editLandingLocationRoute':
    case 'newLandingLocationState':
    case 'editLandingLocationState':
      return PERMISSIONS.has('landing_content:update');
    case 'globalUnavailability':
    case 'jobAvailability':
    case 'warehouseCapacity':
    case 'availabilityLaborAdjustment':
    case 'availabilityRescheduleOffers':
      return PERMISSIONS.has('availability_admin:read');
    case 'regionOperatingScheduleTemplateNew':
    case 'warehouseOperatingScheduleTemplateNew':
      return PERMISSIONS.has('availability_admin:update');
    case 'manageItemCategories':
      return PERMISSIONS.has('item_category_management:read');
    case 'engineeringRoadmap':
      return PERMISSIONS.has('engineering_tools:read');
    case 'selfStorageFacilities':
    case 'selfStorageUnitsIndex':
    case 'selfStorageUnitDetail':
      return PERMISSIONS.has('self_storage:read');
    case 'hiringJobListings':
    case 'hiringRoles':
    case 'hiringInterviewTimeSlots':
    case 'hiringCandidates':
    case 'hiringJobApplicationReview':
    case 'hiringPreScreenQuestions':
      return PERMISSIONS.has('hiring:read');
    case 'newHiringRole':
    case 'editHiringRole':
    case 'newHiringJobListing':
    case 'editHiringJobListing':
    case 'newHiringPreScreenQuestion':
    case 'editHiringPreScreenQuestion':
      return PERMISSIONS.has('hiring:update');
    case null:
      return false;
    default:
      return ROLES.has(UserRole.Admin) || ROLES.has(UserRole.Manager);
  }
};
